// SPDX-License-Identifier: MIT
//
// Subscription-first "Connecter mon Claude" primary onboarding panel. Sits at
// the top of /welcome, above the (demoted, dev-only) API-key form. Per
// genesis-byoai-no-inference-funding, the primary AI connection is the user's
// subscription via `claude login` running in their LOCAL Claude Code — a web
// app can't use a Claude subscription, so this is a warm, OS-aware GUIDE whose
// endpoint is the MCP "Connect my Claude" OAuth handoff (already shipped). The
// genesis MCP server is standard MCP; the coming-soon line signals other clients.
//
// The 4 steps are a guided STEPPER: one step open at a time, a "✓ Fait → suivante"
// button collapses the current step (checkmark in its header) and opens the next,
// so the user focuses only on the step in progress. Active step + completed steps
// persist in localStorage (resume where you left off).

import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import { detectOS, nodeInstallCommand, CLAUDE_CODE_INSTALL_CMD, osLabel, type OS } from '../lib/os-install';
import { markSetupComplete } from '../lib/setup-state';

const MCP_ADD_CMD = 'claude mcp add --transport http genesis https://mcp.genesis.myconciergerie.fr';

// Express transparent one-liner: install Claude Code + connect Genesis MCP.
// `claude login` is interactive (opens a browser) so it is NOT chained here —
// it's shown as a visible 1-click follow-up below the line (see render).
const EXPRESS_CMD = `${CLAUDE_CODE_INSTALL_CMD} && ${MCP_ADD_CMD}`;

// Visit-count gate: expanded for the first couple of /welcome visits (a newcomer
// sees the full guide), then auto-collapses so a returning, already-set-up user
// isn't pushed past it to reach their projects. Mirrors the Mode-découverte
// visit-count pattern in forgejo-next-steps.
const VISITS_LS_KEY = 'genesis.connectClaudeVisits';
const OPEN_FOR_FIRST_N = 2;
function bumpVisitCount(): number {
  try {
    const n = (parseInt(localStorage.getItem(VISITS_LS_KEY) ?? '0', 10) || 0) + 1;
    localStorage.setItem(VISITS_LS_KEY, String(n));
    return n;
  } catch { return 1; }
}

// Stepper progress (active step + completed step indices), persisted so the user
// resumes mid-setup across reloads.
const STEP_LS_KEY = 'genesis.connectClaudeStep';
interface StepState { active: number; done: number[]; }
function loadStepState(): StepState {
  try {
    const s = JSON.parse(localStorage.getItem(STEP_LS_KEY) ?? 'null');
    if (s && typeof s.active === 'number' && Array.isArray(s.done)) return { active: s.active, done: s.done };
  } catch { /* ignore */ }
  return { active: 0, done: [] };
}
function saveStepState(s: StepState): void {
  try { localStorage.setItem(STEP_LS_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

function stepConfigs(os: OS): Array<{ title: string; body: string; cmds: Array<[string, string]> }> {
  return [
    { title: t('connectClaude.step1Title'), body: t('connectClaude.step1Body').replace('{os}', osLabel(os)),
      cmds: [[nodeInstallCommand(os), 'node'], [CLAUDE_CODE_INSTALL_CMD, 'claude-code']] },
    { title: t('connectClaude.step2Title'), body: t('connectClaude.step2Body'), cmds: [['claude login', 'login']] },
    { title: t('connectClaude.step3Title'), body: t('connectClaude.step3Body'), cmds: [[MCP_ADD_CMD, 'mcp-add']] },
  ];
}

export function renderConnectClaude(): HTMLElement {
  const os = detectOS();
  const open = bumpVisitCount() <= OPEN_FOR_FIRST_N;
  const configs = stepConfigs(os);
  const lastIdx = configs.length - 1;
  const wrap = document.createElement('section');
  wrap.className = 'connect-claude';
  wrap.setAttribute('data-component', 'connect-claude');
  const stepsHtml = configs.map((s, i) => `
    <li class="connect-claude__step" data-step="${i}">
      <button type="button" class="connect-claude__step-head" data-step-head="${i}">
        <span class="connect-claude__step-num">${i + 1}</span>
        <span class="connect-claude__step-title">${escapeHtml(s.title)}</span>
      </button>
      <div class="connect-claude__step-body">
        <p>${escapeHtml(s.body)}</p>
        ${s.cmds.map(([cmd, key]) => cmdBlock(cmd, key)).join('')}
        <button type="button" class="connect-claude__step-next" data-step-next="${i}">${escapeHtml(i < lastIdx ? t('connectClaude.stepNext') : t('connectClaude.stepDone'))}</button>
      </div>
    </li>`).join('');
  wrap.innerHTML = `
    <details class="connect-claude__details"${open ? ' open' : ''}>
      <summary class="connect-claude__summary">
        <span class="connect-claude__badge">✨ ${escapeHtml(t('connectClaude.badge'))}</span>
        <span class="connect-claude__title">${escapeHtml(t('connectClaude.title'))}</span>
      </summary>
      <div class="connect-claude__body">
        <p class="connect-claude__wow">${escapeHtml(t('connectClaude.wow'))}</p>
        <p class="connect-claude__byoai">${escapeHtml(t('connectClaude.byoai'))}</p>
        <p class="connect-claude__reassure">${escapeHtml(t('setup.onceForAll'))}</p>

        <div class="setup-modes" role="tablist" aria-label="${escapeHtml(t('connectClaude.title'))}">
          <button type="button" class="setup-mode" role="tab" data-component="setup-mode-express" aria-selected="true">
            <span class="setup-mode__ic" aria-hidden="true">⚡</span>
            <span><span class="setup-mode__title">${escapeHtml(t('setup.modeExpress'))}</span><span class="setup-mode__sub">${escapeHtml(t('setup.modeExpressSub'))}</span></span>
          </button>
          <button type="button" class="setup-mode" role="tab" data-component="setup-mode-guided" aria-selected="false">
            <span class="setup-mode__ic" aria-hidden="true">🗺️</span>
            <span><span class="setup-mode__title">${escapeHtml(t('setup.modeGuided'))}</span><span class="setup-mode__sub">${escapeHtml(t('setup.modeGuidedSub'))}</span></span>
          </button>
        </div>

        <div class="setup-panel is-shown" data-panel="express" data-component="setup-express">
          ${cmdBlockDark(EXPRESS_CMD, 'express')}
          <p class="connect-claude__login-follow">${escapeHtml(t('connectClaude.step2Body'))}</p>
          ${cmdBlockDark('claude login', 'express-login')}
          <details class="connect-claude__nonode">
            <summary>${escapeHtml(t('setup.noNode'))}</summary>
            ${cmdBlock(nodeInstallCommand(os), 'express-node')}
          </details>
          <button type="button" class="connect-claude__step-next" data-component="express-done">${escapeHtml(t('connectClaude.stepDone'))}</button>
        </div>

        <div class="setup-panel" data-panel="guided" data-component="setup-guided">
          <ol class="connect-claude__steps">${stepsHtml}</ol>
        </div>

        <p class="connect-claude__sso">${escapeHtml(t('connectClaude.step4Body'))}</p>
        <p class="connect-claude__multimcp">
          ${escapeHtml(t('connectClaude.multiMcp'))}
          <span class="connect-claude__soon">${escapeHtml(t('connectClaude.comingSoon'))}</span>
        </p>
        <div class="carnet-bravo" data-component="cc-bravo">${escapeHtml(t('setup.bravo'))} ★</div>
      </div>
    </details>
  `;
  wireCopyButtons(wrap);
  mountStepper(wrap);
  wireDualMode(wrap);
  return wrap;
}

// Dark "carnet-cmd" command block. The prompt "$" is a sibling span (NOT inside
// <code>) so the copy button grabs only the command text. wireCopyButtons reads
// the cc-cmd-<key> code, so this stays compatible with the existing copy plumbing.
function cmdBlockDark(cmd: string, key: string): string {
  return `
    <div class="carnet-cmd">
      <span class="carnet-cmd__prompt" aria-hidden="true">$</span>
      <code data-component="cc-cmd-${key}">${escapeHtml(cmd)}</code>
      <button type="button" data-component="cc-copy-${key}" class="carnet-cmd__copy" aria-label="${escapeHtml(t('connectClaude.copyAria'))}">📋</button>
    </div>
  `;
}

// Wire the Express/Guided tab toggle + the Express "c'est fait" button. The
// guided stepper's completion is wired in mountStepper.
function wireDualMode(wrap: HTMLElement): void {
  const panels = Array.from(wrap.querySelectorAll<HTMLElement>('.setup-panel'));
  const tabs = Array.from(wrap.querySelectorAll<HTMLButtonElement>('[data-component^="setup-mode-"]'));
  const select = (mode: 'express' | 'guided'): void => {
    tabs.forEach((tb) => tb.setAttribute('aria-selected', String(tb.getAttribute('data-component') === `setup-mode-${mode}`)));
    panels.forEach((p) => p.classList.toggle('is-shown', p.getAttribute('data-panel') === mode));
  };
  tabs.forEach((tb) => tb.addEventListener('click', () => {
    select(tb.getAttribute('data-component') === 'setup-mode-guided' ? 'guided' : 'express');
  }));
  wrap.querySelector<HTMLButtonElement>('[data-component="express-done"]')?.addEventListener('click', () => {
    markSetupComplete();
    flashBravo(wrap);
  });
}

// Pop the Caveat "bravo" stamp for ~1.4s.
function flashBravo(wrap: HTMLElement): void {
  const bravo = wrap.querySelector('[data-component="cc-bravo"]');
  if (!bravo) return;
  bravo.classList.add('is-shown');
  setTimeout(() => bravo.classList.remove('is-shown'), 1400);
}

// Apply active/done state to the step items + wire header clicks (jump to a step)
// and "suivante" buttons (mark current done, open the next). State persists.
function mountStepper(wrap: HTMLElement): void {
  const items = Array.from(wrap.querySelectorAll<HTMLElement>('.connect-claude__step'));
  if (items.length === 0) return;
  let state = loadStepState();
  const apply = (): void => {
    items.forEach((li, i) => {
      li.classList.toggle('is-active', i === state.active);
      const done = state.done.includes(i);
      li.classList.toggle('is-done', done);
      const num = li.querySelector('.connect-claude__step-num');
      if (num) num.textContent = done ? '✓' : String(i + 1);
    });
  };
  wrap.querySelectorAll<HTMLButtonElement>('[data-step-head]').forEach((h) => {
    h.addEventListener('click', () => {
      state = { active: Number(h.getAttribute('data-step-head')), done: state.done };
      saveStepState(state); apply();
    });
  });
  wrap.querySelectorAll<HTMLButtonElement>('[data-step-next]').forEach((b) => {
    b.addEventListener('click', () => {
      const i = Number(b.getAttribute('data-step-next'));
      const done = state.done.includes(i) ? state.done : [...state.done, i];
      state = { active: Math.min(i + 1, items.length - 1), done };
      saveStepState(state); apply();
      // Finishing the last guided step = the machine is Genesis-ready → flip the
      // dashboard dispatch flag + celebrate.
      if (done.length === items.length) { markSetupComplete(); flashBravo(wrap); }
    });
  });
  apply();
}

function cmdBlock(cmd: string, key: string): string {
  return `
    <div class="connect-claude__cmd">
      <code data-component="cc-cmd-${key}">${escapeHtml(cmd)}</code>
      <button type="button" data-component="cc-copy-${key}" class="connect-claude__copy" aria-label="${escapeHtml(t('connectClaude.copyAria'))}">📋</button>
    </div>
  `;
}

function wireCopyButtons(root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>('[data-component^="cc-copy-"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const key = btn.getAttribute('data-component')!.replace(/^cc-copy-/, '');
      const code = root.querySelector<HTMLElement>(`[data-component="cc-cmd-${key}"]`);
      if (!code) return;
      const ok = await copyText(code.textContent ?? '');
      const original = btn.textContent ?? '📋';
      btn.textContent = ok ? '✓' : '✗';
      setTimeout(() => { btn.textContent = original; }, 1600);
    });
  });
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* fall through to false */ }
  return false;
}
